<script setup lang="ts">
/**
 * Boîte dont la hauteur suit son contenu EN DOUCEUR (Karim 02/11/2026 :
 * « l'ouverture n'est pas très smooth ») : à l'ouverture, à la fermeture, et
 * aussi quand le contenu change de taille une fois ouvert (autre moyen de
 * paiement choisi, formulaire de Stripe qui se charge, message d'erreur).
 * La hauteur est MESURÉE (ResizeObserver), jamais devinée. Fondu seul si
 * l'appareil demande moins d'animations.
 *
 * Fermée, la boîte garde son contenu le temps de se refermer : c'est au
 * parent de le retirer à `referme` (le retirer tout de suite le ferait
 * disparaître d'un coup, avant que la boîte ne bouge).
 */
const props = defineProps<{ ouvert: boolean }>();
const emit = defineEmits<{ referme: [] }>();

/** Durée de l'animation, la même que `duration-300` ci-dessous. */
const DUREE_MS = 300;

const contenu = ref<HTMLElement | null>(null);
/** Hauteur mesurée du contenu ; null tant qu'elle n'a pas pu l'être. */
const hauteur = ref<number | null>(null);
let observateur: ResizeObserver | null = null;
let minuteur: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
  if (!contenu.value || typeof ResizeObserver === "undefined") return;
  observateur = new ResizeObserver(() => {
    hauteur.value = contenu.value?.offsetHeight ?? 0;
  });
  observateur.observe(contenu.value);
});

onBeforeUnmount(() => {
  observateur?.disconnect();
  clearTimeout(minuteur);
});

watch(
  () => props.ouvert,
  (ouvert) => {
    clearTimeout(minuteur);
    if (!ouvert) minuteur = setTimeout(() => emit("referme"), DUREE_MS);
  },
);

const style = computed(() => {
  if (!props.ouvert) return { height: "0px" };
  // Pas encore mesurée (ou navigateur sans ResizeObserver) : hauteur naturelle.
  return { height: hauteur.value === null ? "auto" : `${hauteur.value}px` };
});
</script>

<template>
  <div
    class="overflow-hidden transition-[height,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-opacity"
    :class="ouvert ? 'opacity-100' : 'opacity-0'"
    :style="style"
    :aria-hidden="ouvert ? undefined : 'true'"
    :inert="!ouvert || undefined"
  >
    <!-- flow-root : les marges du contenu restent DANS la mesure (sinon
         elles débordent et le bas du contenu est coupé). -->
    <div ref="contenu" class="flow-root">
      <slot />
    </div>
  </div>
</template>
